/** @jsxImportSource theme-ui @jsxRuntime classic */
import * as Icons from '@tishman/icons';
import React from 'react';

import { useLocalizedLink } from '../../../rockefellercenter/src/utils/use-localized-link';
import { Link, Box, Text, Grid, useThemeUI } from '..';

export interface FooterMenuProps {
  /**
   * Displays a list of links in a footer menu.
   * @param {Object[]} menuLinks - list of menu link groups
   * @param {string} menuLinks[].label - group label
   * @param {string} menuLinks[].url - url to group landing page
   * @param {string} menuLinks[].links[LinkProp] - list of objects that have
   * a label and "url" url.
   */
  menuLinks: MenuLinkGroup[][];
}

export interface MenuLinkGroup {
  heading: string;
  url: string;
  label: string;
  links: LinkProp[];
}

interface LinkProp {
  label: string;
  url: string;
}

export const FooterMenu = ({
  menuLinks,
}: FooterMenuProps): React.JSX.Element => {
  const getLocalizedLink = useLocalizedLink();
  const themeUI = useThemeUI();

  return (
    <Grid
      columns={['1fr', 'repeat(auto-fit, minmax(200px, 1fr))']}
      sx={{ rowGap: [0, null, 4], columnGap: [0, 7, 8], mt: [4] }}
    >
      {menuLinks.map((linkGroup, i) => (
        <ul key={`${linkGroup?.[0].heading}-${i}`}>
          {linkGroup.map(({ links, heading: title, label, url: toAll }, i) => (
            <Box key={`${label}-${i}`} sx={{ mb: [4] }}>
              <Text
                as="li"
                key={`${title}-${i}`}
                sx={{ mb: [2, 1] }}
                variant="menuHeading"
              >
                <span aria-level={2} role="heading">
                  {title}
                </span>
              </Text>
              {links.map(({ label, url }, i) => (
                <Box key={`${label}-${i}`}>
                  <Link
                    sx={{
                      fontWeight: 'medium',
                      fontSize: [2, 3],
                      lineHeight: 'footer',
                    }}
                    to={getLocalizedLink(url)}
                    variant="footer"
                  >
                    {label}
                    {label?.includes('Learn More') && (
                      <span className="sr-only">{` about ${title}`}</span>
                    )}
                  </Link>
                </Box>
              ))}
              <Link
                sx={{
                  textTransform: 'none',
                  fontSize: 14,
                  fontWeight: 'medium',
                  letterSpacing: '-0.52px',
                }}
                to={getLocalizedLink(toAll)}
                variant="underline"
              >
                {label}
                <span className="sr-only">{` about ${title}`}</span>
                <Icons.Arrow
                  aria-hidden
                  style={{
                    marginLeft: themeUI.theme.space[2],
                    paddingTop: '2px',
                  }}
                />
              </Link>
            </Box>
          ))}
        </ul>
      ))}
    </Grid>
  );
};
